import { Injectable, EventEmitter, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, defer, fromEvent } from 'rxjs';
import { finalize, map, takeUntil, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { WatchedInterval, mergeWatchedInterval, sumWatchedIntervals } from '../shared/utils/watched-intervals.utils';
import { parseBackendNumber } from '../shared/utils/backend-number.utils';

/**
 * SIMPLIFIED Video Progress Service
 *
 * This service tracks two key values:
 * 1. lastPosition - where the user stopped (for resume)
 * 2. furthestPosition - max position reached (for progress %)
 *
 * All complexity has been removed. No heartbeats, no sessions, just simple saves.
 */

export interface VideoProgress {
  lastPosition: number;
  furthestPosition: number;
  duration: number;
  watchPercentage: number;
  videoCompleted: boolean;  // New separated field (Migration 045)
  completed: boolean;       // Backward compatibility
  shouldResume: boolean;
}

export interface VideoProgressUpdate {
  courseId: string;
  lastPosition: number;
  furthestPosition: number;
  duration: number;  // PHASE 2: Current video duration from player (sent with every update)
  forceComplete?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class VideoProgressService implements OnDestroy {
  private apiUrl = `${environment.apiUrl}/progress/video`;
  private currentModuleId: string | null = null;
  private currentCourseId: string | null = null;
  private videoElement: HTMLVideoElement | null = null;

  // Track positions
  private currentPosition = 0;
  // KAN-306 round 5: furthestPosition is now a DERIVED value, recomputed by
  // recomputeFurthestPosition() as `persistedSeedSeconds +
  // sumWatchedIntervals(sessionIntervals)`. It is kept as a plain field
  // (rather than a getter) so every existing external consumer
  // (saveProgressNow, getProgressSnapshot, handleBeforeUnload, tests) keeps
  // working unchanged -- only the internal computation changed.
  private furthestPosition = 0;
  // The credited-seconds baseline this session started from (the backend's
  // furthest_position_seconds at startTracking time, or an adopted-higher
  // value from a later reconciliation -- see processQueue()).
  private persistedSeedSeconds = 0;
  // Merged [start, end] ranges of currentTime genuinely covered THIS
  // session (see watched-intervals.utils.ts). Replaying a range already
  // inside this list contributes zero additional credited seconds -- this
  // is what defeats the "loop a short segment to inflate progress" exploit
  // that a blind running sum could not detect.
  private sessionIntervals: WatchedInterval[] = [];
  private videoDuration = 0;

  // PHASE 4: Position-based throttling for better reliability
  // Save when position changes by at least 5 seconds OR time threshold reached
  private lastSaveTime = 0;
  private lastSavedPosition = 0; // Track last saved position for position-based throttling
  private readonly SAVE_INTERVAL_MS = 10000;
  private readonly SHORT_VIDEO_THRESHOLD = 30; // Videos under 30 seconds are "short"
  private readonly SHORT_VIDEO_SAVE_INTERVAL = 3000; // Save every 3 seconds for short videos
  private readonly POSITION_DELTA_THRESHOLD = 5; // Save when position changes by 5+ seconds

  // Track if user has actually started playing
  private hasUserPlayed = false;

  // PHASE 4.4: Store initial watch percentage for duration change migration
  private initialWatchPercentage?: number;

  // Event emitter for module completion
  public moduleCompleted = new Subject<string>();  // Emits moduleId when completed
  /**
   * KAN-597: lifecycle of a save that is expected to COMPLETE a module - the
   * watch requirement was just met on screen. `pending: true` when it is sent,
   * `pending: false` once it has settled, whatever the outcome (completed, not
   * completed, failed). A completing response emits `moduleCompleted` first.
   * Lets the player hold a neutral "Saving progress..." footer for exactly the
   * request's lifetime instead of a stale "Complete Module".
   */
  public completionSaveState = new Subject<{ moduleId: string; pending: boolean }>();
  // Module whose completion save is queued or in flight; settled on queue drain.
  private completionSaveModuleId: string | null = null;

  // PHASE 1.1: Request Queue with Max-Value Deduplication
  // Accumulates save attempts and sends highest position values
  // This prevents data loss when position changes while save is in flight
  private saveQueue: Array<{
    furthestPosition: number;
    lastPosition: number;
    timestamp: number;
  }> = [];
  private isSaving = false;
  // Skip redundant saves after video completion is persisted to DB
  private videoCompleteSaved = false;

  // Track player mode - determines if progress should be saved
  private currentPlayerMode: 'learner' | 'instructor_preview' | 'instructor_test' = 'learner';

  // PHASE 3: RxJS Lifecycle Management
  // Service-level lifecycle (called when Angular destroys the service)
  private destroy$ = new Subject<void>();
  // Video-level lifecycle (called when stopTracking is called to switch videos)
  private stopVideo$ = new Subject<void>();

  constructor(private http: HttpClient) {}

  /**
   * Get video progress for a module
   * @param moduleId - The module to fetch progress for
   * @param courseId - Optional. When provided, filters by course to prevent
   *   cross-course progress contamination when a module exists in multiple courses.
   */
  getVideoProgress(moduleId: string, courseId?: string): Observable<VideoProgress> {
    let url = `${this.apiUrl}/${moduleId}`;
    if (courseId) {
      url += `?courseId=${encodeURIComponent(courseId)}`;
    }
    return this.http.get<any>(url).pipe(
      map(response => {
        // Handle wrapped response format
        let data = response;
        if (response && typeof response === 'object' && 'success' in response && 'data' in response) {
          data = response.data;
        }

        // Backend returns camelCase after transformation. KAN-306 round 6
        // (CRITICAL, live dev): Postgres numeric columns are serialized as
        // STRINGS -- `?? 0` does NOT coerce a non-null string, so every
        // numeric field here MUST go through parseBackendNumber() at this
        // single response boundary. This is the exact gap that let
        // persistedSeedSeconds seed as a string and turn the round-5
        // interval engine's `seed + coverage` into string concatenation.
        const lastPosition = parseBackendNumber(data.lastPosition, 0);
        return {
          lastPosition,
          furthestPosition: parseBackendNumber(data.furthestPosition, 0),
          duration: parseBackendNumber(data.duration, 0),
          watchPercentage: parseBackendNumber(data.watchPercentage, 0),
          videoCompleted: data.videoCompleted ?? false,
          completed: data.completed || false,
          shouldResume: lastPosition > 5 // Resume if more than 5 seconds in
        };
      })
    );
  }

  /**
   * Update video progress - simple POST.
   *
   * KAN-206: also emits `moduleCompleted$` when the backend response carries a
   * non-null `completedAt` (the DB trigger fires `completed_at` on the upsert
   * RETURNING row when watch requirement + quiz requirement are satisfied).
   * Centralizing the emission here means every code path that POSTs progress —
   * including the watch-requirement-None auto-complete in
   * `video-progress-handler.ts` — reactively updates the sidebar checkmark
   * without a page refresh.
   */
  updateVideoProgress(moduleId: string, progress: VideoProgressUpdate): Observable<any> {
    return this.http.post(`${this.apiUrl}/${moduleId}`, progress).pipe(
      tap(response => {
        const data = (response as any)?.data || response;
        const completedAtRaw = data?.completedAt ?? data?.completed_at ?? null;
        if (completedAtRaw && moduleId) {
          this.moduleCompleted.next(moduleId);
        }
      })
    );
  }

  /**
   * Wave 1c: Public setter for the player-mode singleton state.
   *
   * This service is provided in 'root', so `currentPlayerMode` persists across
   * component lifecycles. If an instructor preview closes without resetting the
   * mode, a subsequent learner session in the same tab would silently skip
   * backend progress saves because `saveProgressNow()` / `handleBeforeUnload()`
   * short-circuit on `instructor_preview` / `instructor_test`.
   *
   * Mounts that drive video playback MUST call this on init to re-sync the
   * singleton with their own mode. Modals/containers that host an instructor
   * preview SHOULD call this with 'learner' on close as a belt-and-suspenders
   * guard for teardown paths that don't mount a new player.
   */
  updatePlayerMode(mode: 'learner' | 'instructor_preview' | 'instructor_test'): void {
    this.currentPlayerMode = mode;
  }

  /**
   * Read the currently-tracked player mode. Exposed primarily for testability
   * and diagnostic logging.
   */
  getPlayerMode(): 'learner' | 'instructor_preview' | 'instructor_test' {
    return this.currentPlayerMode;
  }

  /**
   * Start tracking a video element
   * IMPORTANT: This is now async to properly await cleanup of previous tracking
   * PHASE 4.4: Added initialWatchPercentage for duration change migration
   */
  async startTracking(
    videoElement: HTMLVideoElement,
    moduleId: string,
    courseId: string,
    initialFurthestPosition: number = 0,
    playerMode: 'learner' | 'instructor_preview' | 'instructor_test' = 'learner',
    initialWatchPercentage?: number  // PHASE 4.4: Optional percentage for smart migration
  ): Promise<void> {
    console.log('[VideoProgress] Starting tracking:', { moduleId, courseId, initialFurthestPosition, playerMode });

    // RACE CONDITION FIX: Await cleanup of previous tracking before starting new tracking
    // This prevents progress loss when navigating between videos quickly
    await this.stopTracking(); // Clean up any previous tracking

    // PHASE 3: Reset video-level lifecycle Subject for new video
    // This allows us to clean up video-specific subscriptions when switching videos
    this.stopVideo$ = new Subject<void>();

    this.videoElement = videoElement;
    this.currentModuleId = moduleId;
    this.currentCourseId = courseId;
    this.currentPlayerMode = playerMode;
    // KAN-306 round 5: seed the interval-tracking baseline for this session
    // rather than assigning furthestPosition directly -- it's recomputed
    // from persistedSeedSeconds + sessionIntervals from now on.
    // KAN-306 round 6 (CRITICAL): coerce defensively at this entry point too
    // -- this is the exact field that becomes the left operand of every
    // future `seed + coverage` computation, so any caller that passes a
    // stringly-typed value (a pg-numeric-as-string that slipped past an
    // upstream response boundary) must not be able to turn that arithmetic
    // into string concatenation.
    this.persistedSeedSeconds = parseBackendNumber(initialFurthestPosition, 0);
    this.sessionIntervals = [];
    this.furthestPosition = this.persistedSeedSeconds;
    this.hasUserPlayed = false;
    this.videoCompleteSaved = false;
    // PHASE 4.4: Store percentage for duration change migration
    this.initialWatchPercentage = initialWatchPercentage;

    // Capture duration immediately if available, or wait for loadedmetadata
    if (videoElement.duration && !isNaN(videoElement.duration)) {
      this.videoDuration = videoElement.duration;
      console.log('[VideoProgress] Duration captured immediately:', this.videoDuration);

      // PHASE 4.4: Smart duration change migration
      // When video is replaced with different duration, preserve watch percentage
      if (this.furthestPosition > this.videoDuration) {
        console.log('[VideoProgress] Video duration changed - position exceeds new duration:', {
          oldPosition: this.furthestPosition,
          newDuration: this.videoDuration
        });

        // If we have the watch percentage from backend, use it to preserve progress
        if (this.initialWatchPercentage !== undefined && this.initialWatchPercentage > 0) {
          this.persistedSeedSeconds = (this.initialWatchPercentage / 100) * this.videoDuration;
          console.log('[VideoProgress] Preserved watch percentage:', {
            percentage: this.initialWatchPercentage,
            newPosition: this.persistedSeedSeconds
          });
        } else {
          // Fallback: cap to duration (prevents errors but loses exact percentage)
          this.persistedSeedSeconds = this.videoDuration;
          console.log('[VideoProgress] No percentage available, capping to duration');
        }
        // The old session-local coverage was measured against the PREVIOUS
        // duration and is no longer meaningful against the new one.
        this.sessionIntervals = [];
        this.recomputeFurthestPosition();
      }
    } else {
      // PHASE 3: Use RxJS fromEvent instead of addEventListener
      fromEvent(videoElement, 'loadedmetadata')
        .pipe(takeUntil(this.stopVideo$))
        .subscribe(() => this.handleMetadataLoaded());
    }

    // PHASE 3: Replace all addEventListener with RxJS fromEvent + takeUntil
    // This provides automatic cleanup when stopTracking() is called (video switch)
    fromEvent(videoElement, 'play')
      .pipe(takeUntil(this.stopVideo$))
      .subscribe(() => this.handlePlay());

    fromEvent(videoElement, 'timeupdate')
      .pipe(takeUntil(this.stopVideo$))
      .subscribe(() => this.handleTimeUpdate());

    fromEvent(videoElement, 'pause')
      .pipe(takeUntil(this.stopVideo$))
      .subscribe(() => this.handlePause());

    fromEvent(videoElement, 'ended')
      .pipe(takeUntil(this.stopVideo$))
      .subscribe(() => this.handleEnded());

    // Page unload still uses addEventListener because it's on window, not videoElement
    // We'll clean this up in stopTracking() as before
    window.addEventListener('beforeunload', this.handleBeforeUnload);
  }

  /**
   * Handle metadata loaded - capture duration
   */
  private handleMetadataLoaded = (): void => {
    if (this.videoElement && this.videoElement.duration && !isNaN(this.videoElement.duration)) {
      this.videoDuration = this.videoElement.duration;
      console.log('[VideoProgress] Duration captured from metadata:', this.videoDuration);

      // PHASE 4.4: Smart duration change migration
      // When video is replaced with different duration, preserve watch percentage
      if (this.furthestPosition > this.videoDuration) {
        console.log('[VideoProgress] Video duration changed - position exceeds new duration:', {
          oldPosition: this.furthestPosition,
          newDuration: this.videoDuration
        });

        // If we have the watch percentage from backend, use it to preserve progress
        if (this.initialWatchPercentage !== undefined && this.initialWatchPercentage > 0) {
          this.persistedSeedSeconds = (this.initialWatchPercentage / 100) * this.videoDuration;
          console.log('[VideoProgress] Preserved watch percentage:', {
            percentage: this.initialWatchPercentage,
            newPosition: this.persistedSeedSeconds
          });
        } else {
          // Fallback: cap to duration (prevents errors but loses exact percentage)
          this.persistedSeedSeconds = this.videoDuration;
          console.log('[VideoProgress] No percentage available, capping to duration');
        }
        this.sessionIntervals = [];
        this.recomputeFurthestPosition();
      }
    }
  };

  /**
   * KAN-306 round 5: single source of truth for furthestPosition. Always
   * derives it from the stable seed plus this session's merged, deduped
   * interval coverage -- never mutated by `+=` directly, which is what let
   * a per-tick rounding/replay bias silently compound across many ticks.
   */
  private recomputeFurthestPosition(): void {
    this.furthestPosition = this.persistedSeedSeconds + sumWatchedIntervals(this.sessionIntervals);
  }

  /**
   * Handle play event
   */
  private handlePlay = (): void => {
    this.hasUserPlayed = true;
    console.log('[VideoProgress] User started playing video');
  };

  /**
   * Handle time update - track position and save periodically
   *
   * KAN-306 round 2: furthestPosition (the value actually SENT to and
   * PERSISTED by the backend via GREATEST -- permanent, never lowered) only
   * credits small-step forward playback, NOT a raw high-water mark of
   * currentTime. The previous "if currentPosition > furthestPosition" logic
   * let a single forward seek/skip instantly inflate the persisted value to
   * the scrubber position, since ANY forward movement -- jump or genuine
   * playback -- advanced it identically. Only a small per-tick delta (real
   * continuous playback) earns credit; a big jump earns nothing, and
   * playback picks up crediting again from wherever it lands, without
   * retroactively crediting the skipped span.
   *
   * KAN-306 round 5: round-3/4 QA proved this was still a blind running SUM
   * of deltas (`+= delta`), and wire evidence showed it inflating ~15-17%
   * above real elapsed time -- decisively proven by a "replay already-
   * watched content" test where furthestPosition kept climbing every tick
   * regardless of the (low, already-covered) currentTime, eventually
   * reaching 100% while ~2 minutes of the video were never watched. A
   * running sum has no notion of "already counted": replaying the same
   * small span N times credits it N times. Genuine forward-step deltas are
   * now merged into `sessionIntervals` (see watched-intervals.utils.ts)
   * instead of summed directly, so replaying a span already inside this
   * session's tracked coverage credits exactly zero additional seconds.
   */
  private handleTimeUpdate = (): void => {
    if (!this.videoElement || !this.hasUserPlayed) {
      return;
    }

    const previousPosition = this.currentPosition;
    this.currentPosition = this.videoElement.currentTime;
    this.videoDuration = this.videoElement.duration;

    const delta = this.currentPosition - previousPosition;
    if (delta > 0 && delta < 2) {
      this.sessionIntervals = mergeWatchedInterval(this.sessionIntervals, previousPosition, this.currentPosition);
    }
    // On rewind or a big forward jump, nothing is credited. Either way,
    // `currentPosition` above already reflects the new position, so the
    // NEXT tick's delta is computed from here -- there is no separate
    // "frozen" baseline that a rewind could get permanently stuck behind.
    this.recomputeFurthestPosition();

    // Save progress every 10 seconds (throttled)
    this.saveProgressThrottled();
  };

  /**
   * Handle pause - save immediately
   */
  private handlePause = (): void => {
    if (this.hasUserPlayed) {
      this.saveProgressNow();
    }
  };

  /**
   * Handle video end - save and mark complete
   *
   * KAN-306 round 2: previously this unconditionally forced furthestPosition
   * to the FULL duration whenever the native "ended" event fired -- but
   * "ended" fires once currentTime reaches duration REGARDLESS of how it got
   * there. A learner who skipped through most of the video and let the last
   * few seconds play out would have their true (much lower) credited
   * coverage overwritten with 100% the instant the video ended, then
   * persisted permanently via forceComplete+GREATEST. Instead, only credit
   * the final small gap between the last tracked position and duration --
   * this still fixes the original floating-point issue this method was
   * added for (currentTime=12.0 vs duration=12.15), without blanket-crediting
   * content that was never actually watched.
   */
  private handleEnded = (): void => {
    if (this.hasUserPlayed) {
      if (this.videoElement && this.videoDuration > 0) {
        const finalGap = this.videoDuration - this.currentPosition;
        if (finalGap > 0 && finalGap < 2) {
          this.sessionIntervals = mergeWatchedInterval(this.sessionIntervals, this.currentPosition, this.videoDuration);
        }
        this.currentPosition = this.videoDuration;
        this.recomputeFurthestPosition();
        console.log('[VideoProgress] Video ended - crediting final gap to duration:', {
          furthestPosition: this.furthestPosition,
          duration: this.videoDuration
        });
      }
      this.saveProgressNow(true);
    }
  };

  /**
   * PHASE 4: Handle before unload with navigator.sendBeacon
   * Uses sendBeacon for more reliable progress saves during page unload
   */
  private handleBeforeUnload = (): void => {
    if (!this.hasUserPlayed || !this.currentModuleId || !this.currentCourseId) {
      return;
    }

    // Skip saving for practice mode or instructor modes
    if (this.currentCourseId === 'practice-mode' ||
        this.currentPlayerMode === 'instructor_preview' ||
        this.currentPlayerMode === 'instructor_test') {
      return;
    }

    // Ensure we have valid duration
    if (!this.videoDuration || isNaN(this.videoDuration) || this.videoDuration <= 0) {
      return;
    }

    // Prepare progress data
    const safeFurthestPosition = Math.min(this.furthestPosition, this.videoDuration);
    const safeLastPosition = Math.min(this.currentPosition, this.videoDuration);

    const progressData = {
      courseId: this.currentCourseId,
      lastPosition: safeLastPosition,
      furthestPosition: safeFurthestPosition,
      duration: this.videoDuration,
      forceComplete: false
    };

    // PHASE 4: Use navigator.sendBeacon for more reliable page unload saves
    // sendBeacon sends data asynchronously without blocking page unload
    if (navigator.sendBeacon) {
      const url = `${this.apiUrl}/${this.currentModuleId}`;
      const blob = new Blob([JSON.stringify(progressData)], { type: 'application/json' });

      // Get auth token for beacon request
      const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');

      // sendBeacon doesn't support custom headers, so we can't send auth token this way
      // Fallback to regular save which might not complete
      console.log('[VideoProgress] Page unload - attempting save via sendBeacon');

      // Note: sendBeacon has limitations with auth tokens
      // We'll try sendBeacon but also queue a regular save as backup
      try {
        // Try sendBeacon (may fail if auth required)
        const sent = navigator.sendBeacon(url, blob);
        if (sent) {
          console.log('[VideoProgress] sendBeacon sent successfully');
        } else {
          console.warn('[VideoProgress] sendBeacon failed, falling back to regular save');
          this.saveProgressNow();
        }
      } catch (error) {
        console.error('[VideoProgress] sendBeacon error:', error);
        this.saveProgressNow();
      }
    } else {
      // Fallback for browsers without sendBeacon support
      this.saveProgressNow();
    }
  };

  /**
   * PHASE 4: Save progress with position-based throttling
   * Saves when position changes significantly OR time threshold reached
   * This ensures saves happen at meaningful intervals rather than arbitrary time intervals
   */
  private saveProgressThrottled(): void {
    const now = Date.now();
    const positionDelta = Math.abs(this.currentPosition - this.lastSavedPosition);

    // Use shorter save interval for short videos
    const saveInterval = this.videoDuration > 0 && this.videoDuration < this.SHORT_VIDEO_THRESHOLD
      ? this.SHORT_VIDEO_SAVE_INTERVAL
      : this.SAVE_INTERVAL_MS;

    // PHASE 4: Save if EITHER:
    // 1. Time threshold reached, OR
    // 2. Position changed by threshold amount (e.g., user jumped forward/backward)
    const timeThresholdReached = (now - this.lastSaveTime) >= saveInterval;
    const positionThresholdReached = positionDelta >= this.POSITION_DELTA_THRESHOLD;

    if (timeThresholdReached || positionThresholdReached) {
      if (positionThresholdReached) {
        console.log(`[VideoProgress] Position-based save triggered (delta: ${positionDelta.toFixed(1)}s)`);
      }
      this.lastSavedPosition = this.currentPosition;
      this.saveProgressNow();
    }
  }

  /**
   * KAN-597: the watch requirement was just met on screen. Save NOW rather than
   * waiting out the throttle (up to SAVE_INTERVAL_MS), and report the save's
   * lifecycle on `completionSaveState`. Returns false - and reports nothing -
   * when no save is sent (instructor modes, practice mode, no duration yet), so
   * the caller never waits on a request that does not exist.
   */
  saveForCompletion(): boolean {
    const moduleId = this.currentModuleId;
    if (!moduleId) return false;
    // Armed BEFORE the save is queued so the drain can never miss it.
    this.completionSaveModuleId = moduleId;
    if (!this.saveProgressNow()) {
      this.completionSaveModuleId = null;
      return false;
    }
    // Still armed unless the save already settled synchronously.
    if (this.completionSaveModuleId === moduleId) {
      this.completionSaveState.next({ moduleId, pending: true });
    }
    return true;
  }

  /**
   * KAN-597: the same lifecycle for a completion upsert sent outside the queue
   * (the 0%-requirement auto-complete in VideoProgressHandler): pending on
   * subscribe, settled when the request completes, errors, or is unsubscribed.
   */
  trackCompletionSave<T>(moduleId: string, source: Observable<T>): Observable<T> {
    return defer(() => {
      this.completionSaveState.next({ moduleId, pending: true });
      return source.pipe(finalize(() => this.completionSaveState.next({ moduleId, pending: false })));
    });
  }

  /**
   * Save progress immediately - with queue-based max-value deduplication
   * PHASE 1.1: Accumulates rapid saves and sends highest position values
   * This prevents data loss when position changes while save is in flight
   *
   * Returns whether the save was queued (KAN-597: saveForCompletion needs it).
   */
  private saveProgressNow(forceComplete: boolean = false): boolean {
    if (!this.videoElement || !this.currentModuleId || !this.currentCourseId || !this.hasUserPlayed) {
      return false;
    }

    // Skip redundant saves after completion is already persisted
    if (this.videoCompleteSaved && !forceComplete) {
      console.log('[VideoProgress] Skipping save - video completion already persisted');
      return false;
    }

    // Skip saving for practice mode
    if (this.currentCourseId === 'practice-mode') {
      return false;
    }

    // Skip saving for instructor preview/test modes
    // This prevents instructor's test progress from polluting the database
    if (this.currentPlayerMode === 'instructor_preview' || this.currentPlayerMode === 'instructor_test') {
      console.log('[VideoProgress] Skipping save - instructor mode:', this.currentPlayerMode);
      return false;
    }

    // Ensure we have a valid duration before saving
    // Video duration might be NaN or 0 if metadata hasn't loaded yet
    if (!this.videoDuration || isNaN(this.videoDuration) || this.videoDuration <= 0) {
      console.log('[VideoProgress] Skipping save - invalid duration:', this.videoDuration);
      return false;
    }

    // DEFENSIVE: Ensure furthest position never exceeds duration
    // This prevents 500 errors from backend constraint violations
    const safeFurthestPosition = Math.min(this.furthestPosition, this.videoDuration);
    const safeLastPosition = Math.min(this.currentPosition, this.videoDuration);

    if (safeFurthestPosition !== this.furthestPosition) {
      console.warn('[VideoProgress] Capping furthestPosition to duration:', {
        original: this.furthestPosition,
        capped: safeFurthestPosition,
        duration: this.videoDuration
      });
    }

    // PHASE 1.1: Add to queue instead of immediate save
    // Queue accumulates rapid saves and deduplicates by sending max values
    this.saveQueue.push({
      furthestPosition: safeFurthestPosition,
      lastPosition: safeLastPosition,
      timestamp: Date.now()
    });

    console.log('[VideoProgress] Added to save queue (queue size:', this.saveQueue.length, ')');

    // Process the queue (will deduplicate and send max values)
    this.processQueue(forceComplete);
    return true;
  }

  /**
   * PHASE 1.1 & PHASE 3: Process save queue with max-value deduplication
   * Deduplicates rapid saves by taking the highest position values
   * Ensures data is never lost when position changes during save
   */
  private async processQueue(forceComplete: boolean = false): Promise<void> {
    // If already saving or queue is empty, return
    if (this.isSaving || this.saveQueue.length === 0) {
      return;
    }

    // Prevent concurrent processing
    this.isSaving = true;

    try {
      // CRITICAL: Deduplicate by taking MAX values from entire queue
      // This ensures we never lose progress data even if position changes rapidly
      let maxFurthest = Math.max(...this.saveQueue.map(s => s.furthestPosition));
      let maxLast = Math.max(...this.saveQueue.map(s => s.lastPosition));

      // KAN-306 round 3: defense-in-depth backstop. Live QA on dev captured
      // every heartbeat after the first sending "furthestPosition": null
      // (JSON.stringify(NaN) === "null"), silently discarding the learner's
      // progress on every save. Every individual mutation site for
      // furthestPosition is independently guarded against a single bad
      // input producing a non-finite value -- but this is the LAST point
      // before a value leaves the client, so it must never transmit
      // NaN/Infinity regardless of how a bad queue entry could arise. Fall
      // back to the live (always-finite) current state rather than sending
      // a value the backend will persist forever via GREATEST().
      if (!Number.isFinite(maxFurthest)) {
        console.error('[VideoProgress] Non-finite furthestPosition detected in save queue -- falling back to live state', {
          corruptQueue: this.saveQueue,
          fallback: this.furthestPosition
        });
        maxFurthest = this.furthestPosition;
      }
      if (!Number.isFinite(maxLast)) {
        console.error('[VideoProgress] Non-finite lastPosition detected in save queue -- falling back to live state', {
          corruptQueue: this.saveQueue,
          fallback: this.currentPosition
        });
        maxLast = this.currentPosition;
      }

      console.log('[VideoProgress] Processing queue - deduplicating', this.saveQueue.length, 'saves into 1 request with max values:', {
        maxFurthest,
        maxLast
      });

      // Clear queue now that we've captured the max values
      this.saveQueue = [];

      // Ensure we still have valid state
      if (!this.currentModuleId || !this.currentCourseId || !this.videoDuration) {
        console.warn('[VideoProgress] Invalid state during queue processing - skipping save');
        return;
      }

      // Absolute last resort: even the fallback values above must never be
      // sent as non-finite. 0 is always a safe, valid value here -- worse
      // case it under-reports for one save tick, never null/NaN.
      if (!Number.isFinite(maxFurthest)) maxFurthest = 0;
      if (!Number.isFinite(maxLast)) maxLast = 0;

      // KAN-198 follow-up: capture moduleId BEFORE awaiting the HTTP response.
      // Reason: a concurrent stopTracking() (e.g. ngOnDestroy of split-screen-player
      // firing in parallel with the next module mounting) can clear
      // `this.currentModuleId` to null before the promise resolves. The previous
      // code used the (possibly null) instance field at emission time, so the
      // subscriber received `null` and the sidebar never updated.
      const moduleIdForRequest = this.currentModuleId;

      // PHASE 7: Integer-only storage with ceiling strategy (2026-01-29)
      // All positions and durations use Math.ceil() to:
      // 1. Eliminate sub-second precision edge cases
      // 2. Prevent "105% watched" display bugs
      // 3. Ensure completed videos stay completed on resume
      // Build progress data with max values
      const progressData: VideoProgressUpdate = {
        courseId: this.currentCourseId,
        lastPosition: Math.ceil(maxLast),
        furthestPosition: Math.ceil(maxFurthest),
        duration: this.videoDuration > 0 ? Math.ceil(this.videoDuration) : 0,
        forceComplete: forceComplete
      };

      // PHASE 3: Execute save with takeUntil to prevent memory leaks
      const response = await this.updateVideoProgress(moduleIdForRequest, progressData)
        .pipe(takeUntil(this.destroy$))
        .toPromise();

      // CRITICAL: Reconcile local state with backend truth
      // Backend may have adjusted values (e.g., capping to duration, using GREATEST())
      //
      // KAN-306 round 3: live wire-capture regression -- the exit-save sent
      // a STALE lastPosition (403, the video's end position from minutes
      // earlier) even though the learner had since rewound and was actually
      // at 0:21. Root cause: a heartbeat's round trip can still be in
      // flight when the learner rewinds; when that DELAYED response
      // finally arrives, blindly overwriting `currentPosition` with its
      // echoed lastPositionSeconds clobbers the LIVE (already-rewound)
      // local position with state that predates the rewind.
      //
      // currentPosition is NOT a monotonic value -- it legitimately
      // decreases on rewind -- so it must never be reconciled from a
      // response that could be stale by the time it arrives. The freshest
      // DOM-driven reading (updated every handleTimeUpdate/handleEnded
      // tick) is always more authoritative during an active session; only
      // the initial mount (loadExistingProgress, a separate code path) sets
      // the starting resume position from the backend.
      //
      // furthestPosition IS monotonic (backend enforces GREATEST), so it
      // still reconciles -- but via Math.max, not a blind overwrite, for
      // the same reason: a stale response must never regress it below what
      // has already accrued locally since that request was sent.
      //
      // KAN-306 round 5: this used to fold backendFurthest directly into
      // `this.furthestPosition` (the derived field). Under the interval
      // engine that field is recomputed on every tick from
      // persistedSeedSeconds + sessionIntervals -- if the backend's echo is
      // just OUR OWN just-sent value (the common case, no concurrent
      // session), folding it into the seed while leaving sessionIntervals
      // untouched would double-count this session's own coverage on the
      // very next recompute. Only adopt the backend's value when it is
      // STRICTLY HIGHER than our own computed total (proof some other
      // source -- e.g. a concurrent tab -- knows about more credited
      // coverage than we do), and when adopting it, fold it into the seed
      // AND reset sessionIntervals so nothing is counted twice.
      //
      // The comparison is against Math.ceil(this.furthestPosition), NOT the
      // raw fractional value: the backend always echoes back
      // Math.ceil(whatever we just sent) (see progressData below), which is
      // >= our raw fractional running total by construction. Comparing
      // against the raw value would treat that expected, harmless rounding
      // echo as "external advancement" on nearly every single save and
      // adopt it as a new seed -- reintroducing exactly the kind of
      // per-save rounding bias (bounded at just under 1s each time) that
      // wire evidence showed compounding into the round-5 accrual
      // inflation in the first place.
      const data = response?.data || response;
      if (data) {
        // KAN-306 round 6: same string-serialization hazard as the GET
        // boundary above -- this POST-response echo must also be coerced
        // before it can ever become part of an arithmetic seed.
        const backendFurthest = parseBackendNumber(
          data.furthestPositionSeconds ?? data.furthest_position_seconds,
          this.furthestPosition
        );
        if (backendFurthest > Math.ceil(this.furthestPosition)) {
          this.persistedSeedSeconds = backendFurthest;
          this.sessionIntervals = [];
          this.recomputeFurthestPosition();
        }
        console.log('[VideoProgress] State reconciled from backend:', {
          furthestPosition: this.furthestPosition,
          currentPosition: this.currentPosition
        });
      }

      // KAN-206: module-completion emission centralized in `updateVideoProgress`
      // so callers that bypass `processQueue` (e.g. the watch-None auto-complete
      // in `video-progress-handler.ts`) also drive the sidebar checkmark.
      // To preserve the original log line in this hot path, log here when
      // emission is about to happen for the queued save.
      const queuedCompletedAt = data?.completedAt ?? data?.completed_at ?? null;
      if (queuedCompletedAt && moduleIdForRequest) {
        console.log('[VideoProgress] Module completed!', moduleIdForRequest, 'completedAt=', queuedCompletedAt);
      }

      this.lastSaveTime = Date.now();
      console.log('[VideoProgress] Save completed successfully');

      if (forceComplete) {
        this.videoCompleteSaved = true;
        console.log('[VideoProgress] Video completion persisted - future saves will be skipped');
      }

    } catch (error) {
      console.error('[VideoProgress] Save failed:', error);
      // Don't throw - we don't want to block the app on save failures
    } finally {
      this.isSaving = false;

      // CRITICAL: Process any new saves that arrived while this save was in flight
      // This ensures we never lose data even under rapid save conditions
      if (this.saveQueue.length > 0) {
        console.log('[VideoProgress] New saves arrived during processing - recursively processing queue');
        await this.processQueue(forceComplete);
      } else if (this.completionSaveModuleId) {
        // KAN-597: the queue has drained, so the completion save (and anything
        // that was in flight ahead of it) has settled.
        const moduleId = this.completionSaveModuleId;
        this.completionSaveModuleId = null;
        this.completionSaveState.next({ moduleId, pending: false });
      }
    }
  }

  /**
   * Stop tracking and clean up
   * PHASE 1.1: Now properly waits for all saves to complete before cleanup
   */
  async stopTracking(): Promise<void> {
    // Save final progress before cleaning up
    if (this.hasUserPlayed && this.videoElement && this.currentModuleId && this.currentCourseId) {
      console.log('[VideoProgress] Stopping tracking - saving final progress');
      this.saveProgressNow();

      // CRITICAL: Wait for all pending saves to complete
      // This prevents data loss when navigating quickly between modules
      await this.waitForPendingSaves();
      console.log('[VideoProgress] All saves completed, proceeding with cleanup');
    }

    // PHASE 3: Emit stopVideo$ to cleanup all video-specific RxJS subscriptions
    // This unsubscribes from all video element event listeners automatically
    this.stopVideo$.next();
    this.stopVideo$.complete();

    // Still need to manually remove window listener (not managed by RxJS)
    window.removeEventListener('beforeunload', this.handleBeforeUnload);

    // Clear state
    this.videoElement = null;
    this.currentModuleId = null;
    this.currentCourseId = null;
    this.currentPosition = 0;
    this.furthestPosition = 0;
    this.persistedSeedSeconds = 0;
    this.sessionIntervals = [];
    this.videoDuration = 0;
    this.hasUserPlayed = false;
    this.lastSaveTime = 0;
  }

  /**
   * Discard all pending saves WITHOUT flushing them to the backend.
   * Used when progress is reset to prevent stale saves from recreating deleted rows.
   * Unlike stopTracking() which flushes pending saves first, this method
   * silently drops everything in the queue and stops tracking immediately.
   */
  async discardPendingSaves(): Promise<void> {
    console.log('[VideoProgress] Discarding pending saves (queue size:', this.saveQueue.length, ', isSaving:', this.isSaving, ')');

    // Clear the save queue without processing
    this.saveQueue = [];

    // Wait for any in-flight save to complete (we can't cancel HTTP requests)
    // but don't queue any new ones
    const maxWaitTime = 3000;
    const startTime = Date.now();
    while (this.isSaving && (Date.now() - startTime) < maxWaitTime) {
      await new Promise(resolve => setTimeout(resolve, 50));
    }

    // Clear the queue again in case the in-flight save triggered re-queuing
    this.saveQueue = [];

    // Stop all event listeners and clear tracking state
    this.stopVideo$.next();
    this.stopVideo$.complete();
    this.stopVideo$ = new Subject<void>();

    window.removeEventListener('beforeunload', this.handleBeforeUnload);

    // Reset all tracking state
    this.videoElement = null;
    this.currentModuleId = null;
    this.currentCourseId = null;
    this.currentPosition = 0;
    this.furthestPosition = 0;
    this.persistedSeedSeconds = 0;
    this.sessionIntervals = [];
    this.videoDuration = 0;
    this.hasUserPlayed = false;
    this.lastSaveTime = 0;

    console.log('[VideoProgress] All pending saves discarded and tracking stopped');
  }

  /**
   * Check if user has started playing the video
   */
  hasUserStartedVideo(): boolean {
    return this.hasUserPlayed;
  }

  /**
   * Get a snapshot of current video progress for sending with quiz submissions.
   * This allows the backend to write progress before checking the video completion requirement,
   * eliminating the race condition where flushProgress() completes but the DB hasn't updated yet.
   */
  getProgressSnapshot(): { furthestPosition: number; duration: number; courseId: string | null } {
    return {
      furthestPosition: Math.ceil(Math.min(this.furthestPosition, this.videoDuration)),
      duration: this.videoDuration > 0 ? Math.ceil(this.videoDuration) : 0,
      courseId: this.currentCourseId
    };
  }

  /**
   * Calculate watch percentage
   */
  calculateWatchPercentage(furthestPosition: number, duration: number): number {
    if (duration <= 0) return 0;
    return Math.min(100, Math.round((furthestPosition / duration) * 100));
  }

  /**
   * KAN-306 round 5: single source of truth for "how much of this video has
   * been genuinely covered right now" (persisted seed + this session's
   * deduplicated new interval coverage, capped to duration). Exposed so
   * every DISPLAY consumer (VideoProgressHandler's cumulativeWatchTime,
   * which previously maintained its own independent +=-based accumulator)
   * reads the same fixed engine instead of duplicating the accrual logic --
   * two independent implementations was the underlying bug-class risk
   * behind the round-5 accrual inflation.
   */
  getCurrentCoverageSeconds(): number {
    if (this.videoDuration > 0) {
      return Math.min(this.furthestPosition, this.videoDuration);
    }
    return this.furthestPosition;
  }

  /**
   * The rounded integer watch percentage for the current coverage. Both the
   * player-controls inline label and the split-screen status-bar badge must
   * ultimately derive from this single computation (see
   * SplitScreenPlayerComponent.onVideoProgress(), which pushes the same
   * computed integer to both surfaces) -- two independent Math.ceil()
   * calls on values that update at slightly different times is what caused
   * the round-5 "label says 32%, badge says 33%" transient desync.
   */
  getCurrentWatchPercentageInt(): number {
    if (!this.videoDuration || this.videoDuration <= 0) {
      return 0;
    }
    const coverage = this.getCurrentCoverageSeconds();
    if (coverage >= this.videoDuration - 0.5) {
      return 100;
    }
    return Math.min(100, Math.ceil((coverage / this.videoDuration) * 100));
  }

  /**
   * Wait for any pending save operations to complete
   * PHASE 1.1: Updated to work with queue-based approach
   * Processes queue and waits until both queue is empty and no save in progress
   * This is critical before navigation to ensure video progress is saved
   */

  /**
   * Force-save the current video position and wait for all saves to complete.
   * Use this before operations that depend on the backend having the latest progress
   * (e.g., quiz submission that checks video_watch_percentage in the DB).
   */
  async flushProgress(): Promise<void> {
    this.saveProgressNow();
    await this.waitForPendingSaves();
  }

  async waitForPendingSaves(): Promise<void> {
    const maxWaitTime = 5000; // Max 5 seconds wait
    const startTime = Date.now();

    // Keep processing until queue is empty and not saving
    while ((this.saveQueue.length > 0 || this.isSaving) && (Date.now() - startTime) < maxWaitTime) {
      if (this.saveQueue.length > 0 && !this.isSaving) {
        console.log('[VideoProgress] Processing remaining saves in queue...');
        await this.processQueue();
      }

      // If still saving, wait a bit before checking again
      if (this.isSaving || this.saveQueue.length > 0) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }

    if (this.saveQueue.length > 0 || this.isSaving) {
      console.warn('[VideoProgress] Timeout waiting for saves - some may be incomplete');
    } else {
      console.log('[VideoProgress] All pending saves completed');
    }
  }

  /**
   * PHASE 3: Lifecycle cleanup
   * Called when service is destroyed to prevent memory leaks
   */
  ngOnDestroy(): void {
    console.log('[VideoProgress] Service destroying - cleaning up subscriptions');

    // Clean up video-specific subscriptions
    this.stopVideo$.next();
    this.stopVideo$.complete();

    // Clean up service-level subscriptions
    this.destroy$.next();
    this.destroy$.complete();

    // Clean up module completion Subject
    this.moduleCompleted.complete();
  }
}
